import React, { useEffect } from "react";
import { useAskQuestion } from "../../hooks/useLLM";
import { useNewton } from "../../store/NewtonContext";
import { useLlmSessionCreate } from "../../hooks/useLlmSessionCreate";
import { useUserData } from "../../../../../../hooks/useUserData";
import Button from "../../../../../../components/shared/Button/Button";
import { AddDataButton } from "./AddDataButton";
import { AttachFiles } from "./AttachFiles";
import Attachment from "./Attachment";
import Input from "./Input";
import VoiceMic from "./VoiceMic";
import AddSwingsBtn from "./AddSwings/AddSwingsBtn";
import { useMiniSwingLibraryStore } from "../../store/miniSwingLibraryStore";

export function Footer({ textareaRef }) {
  const {
    attachedFiles,
    message,
    messageHtml,
    selectedLibraryVideo,
    sessionId,
    setAttachedFiles,
    setMessage,
    setMessageHtml,
    setSelectedLibraryVideo,
    setVideoFile,
    videoFile,
    videoMetadata,
  } = useNewton();

  const { userData } = useUserData();
  const { setRowSelection } = useMiniSwingLibraryStore();

  const askQuestionMutation = useAskQuestion();
  const createSession = useLlmSessionCreate({ userId: userData?.uid });

  const onSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    const cleanedMessage = (message || "").trim().replaceAll("/", "");
    if (!cleanedMessage) return;

    const userMsg = cleanedMessage || "How's my swing?";

    try {
      setAttachedFiles([]);
      setVideoFile(null);
      setSelectedLibraryVideo(null);
      setRowSelection({});
      setMessage("");
      setMessageHtml("");

      // Auto-create session if user doesn't have one (first-time user)
      if (!sessionId) {
        await createSession.mutateAsync();
      }

      await askQuestionMutation.mutateAsync({
        attachedFiles,
        selectedLibraryVideo,
        userMsg,
        videoFile,
        videoMetadata,
      });
    } catch (err) {
      console.error("askQuestion error:", err);
    }
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  useEffect(() => {
    textareaRef?.current?.focus();
  }, [textareaRef]);

  return (
    <>
      <form
        className="border-grayTrue700 bg-grayTrue800 relative rounded-md border p-4"
        onSubmit={onSubmit}
      >
        <div className="flex flex-wrap gap-1">
          {attachedFiles.length > 0 &&
            attachedFiles.map((f) => (
              <Attachment
                file={f}
                key={`${f.name}-${f.size}-${f.lastModified}`}
                onRemove={() =>
                  setAttachedFiles((prev) =>
                    prev.filter(
                      (item) =>
                        item.name !== f.name ||
                        item.size !== f.size ||
                        item.lastModified !== f.lastModified
                    )
                  )
                }
                type="file"
              />
            ))}

          {videoFile && (
            <Attachment
              onRemove={() => setVideoFile(null)}
              type="video"
              video={videoFile}
            />
          )}

          {selectedLibraryVideo && (
            <Attachment
              onRemove={() => setSelectedLibraryVideo(null)}
              type="video"
              video={selectedLibraryVideo.videoPath}
            />
          )}
        </div>

        <Input
          handleEnterKey={handleEnterKey}
          ref={textareaRef}
          textareaRef={textareaRef}
          value={messageHtml}
        />

        <div className="mt-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AddSwingsBtn />

            <AddDataButton />
          </div>

          <div className="flex items-center justify-end gap-2">
            <VoiceMic lang="en-US" />

            <AttachFiles />

            <Button
              color="white/gray300"
              isDisabled={!message.trim()}
              size="xs"
              type="submit"
            >
              Ask
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
